import { useMutation, useQuery } from "@tanstack/react-query";
import { CreditCard, Gift, Minus, Plus, Receipt, Sparkles } from "lucide-react";
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, errorMessage, unwrap, type S } from "../api/client";
import { useOrg, useOrgId } from "../api/org";
import { useCredits } from "../api/queries";
import { Alert, Badge, Button, Card, Empty, PageHead, Spinner, cx } from "../components/ui";
import { cents, date } from "../lib/format";
import { config } from "../config";

const KIND_LABEL: Record<S["CreditEntryKind"], { label: string; tone?: "ok" | "warn" | "danger" | "info" }> = {
  grant: { label: "Grant", tone: "info" },
  trial: { label: "Trial", tone: "info" },
  purchase: { label: "Purchase", tone: "ok" },
  consume: { label: "Design" },
  refund: { label: "Refund", tone: "ok" },
  expire: { label: "Expired", tone: "warn" },
};

function usePricing() {
  return useQuery({
    queryKey: ["public-pricing"],
    queryFn: async () => {
      const res = await fetch(`${config.apiUrl}/api/public/pricing`);
      if (!res.ok) throw new Error("Pricing unavailable");
      return (await res.json()) as S["Pricing"];
    },
    staleTime: 300_000,
  });
}

function useDebounced<T>(value: T, ms = 250): T {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = window.setTimeout(() => setV(value), ms);
    return () => window.clearTimeout(t);
  }, [value, ms]);
  return v;
}

export function Credits() {
  const orgId = useOrgId();
  const { canManage } = useOrg();
  const [params, setParams] = useSearchParams();
  const credits = useCredits(orgId, 50);
  const pricing = usePricing();
  const [quantity, setQuantity] = useState(100);
  const q = useDebounced(quantity);
  const price = useQuery({
    queryKey: ["price", orgId, q],
    enabled: q >= 1,
    queryFn: () => unwrap(api.GET("/v1/orgs/{org_id}/credits/price", { params: { path: { org_id: orgId }, query: { quantity: q } } })),
    placeholderData: (prev) => prev,
  });
  const checkout = useMutation({
    mutationFn: () =>
      unwrap(
        api.POST("/v1/orgs/{org_id}/credits/checkout", {
          params: { path: { org_id: orgId } },
          body: {
            quantity,
            success_url: `${window.location.origin}/credits?checkout=success`,
            cancel_url: `${window.location.origin}/credits?checkout=cancelled`,
          },
        }),
      ),
    onSuccess: (c) => window.location.assign(c.checkout_url),
  });
  const raw = params.get("checkout");
  const status = raw === "paid" ? "success" : raw === "cancel" ? "cancelled" : raw;
  useEffect(() => {
    if (status === "success") void credits.refetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  if (credits.isLoading) return <Spinner />;
  if (credits.error || !credits.data) return <Alert tone="danger">{errorMessage(credits.error)}</Alert>;
  const p = price.data;
  const tiers = pricing.data?.tiers ?? [];
  const currency = p?.currency ?? pricing.data?.currency ?? "USD";
  const contactOnly = p?.tier.contact_only;
  const used = credits.data.entries.filter((e) => e.kind === "consume").reduce((s, e) => s - e.amount, 0);
  const firstOffer = pricing.data?.first_purchase;
  const firstPurchaseEligible = p ? p.first_purchase : false;

  return (
    <>
      <PageHead title="Credits & billing" description="One credit designs one new roof. Re-quoting the same roof is free." />
      {status === "success" && (
        <div style={{ marginBottom: 16 }}>
          <Alert tone="ok" title="Payment received." action={<Button size="sm" variant="ghost" onClick={() => setParams({})}>Dismiss</Button>}>
            Your credits are added as soon as the payment provider confirms it (usually seconds).
          </Alert>
        </div>
      )}
      {status === "cancelled" && (
        <div style={{ marginBottom: 16 }}>
          <Alert tone="warn" title="Checkout cancelled." action={<Button size="sm" variant="ghost" onClick={() => setParams({})}>Dismiss</Button>}>
            Nothing was charged.
          </Alert>
        </div>
      )}
      <div className="grid-3" style={{ marginBottom: 24 }}>
        <div className="card stat">
          <div className="label">Balance</div>
          <div className="value gradient-text" style={{ fontSize: 34 }}>{credits.data.balance}</div>
          <div className="sub">design credits available</div>
        </div>
        <div className="card stat">
          <div className="label">Designs (recent)</div>
          <div className="value">{used}</div>
          <div className="sub">in the last {credits.data.entries.length} ledger entries</div>
        </div>
        <div className="card stat">
          <div className="label">Trial</div>
          <div className="value" style={{ fontSize: 20 }}>{credits.data.trial_ends_at ? date(credits.data.trial_ends_at) : "—"}</div>
          <div className="sub">{credits.data.trial_ends_at ? "trial credits expire" : "no active trial"}</div>
        </div>
      </div>

      <div className="split-wide">
        <Card title="Buy design credits" description="Volume pricing: the more you buy, the less each design costs.">
          <div className="stack-lg">
            {firstPurchaseEligible && firstOffer && firstOffer.discount_pct > 0 && (
              <div className="offer-banner">
                <Gift />
                <div>
                  <b>First purchase: {firstOffer.discount_pct}% off</b>
                  <div>
                    <span>On up to {firstOffer.max_designs} designs of your first order.</span>
                  </div>
                </div>
              </div>
            )}
            {tiers.length > 0 && (
              <div className="tiers">
                {tiers.map((t) => (
                  <button
                    key={t.id}
                    className={cx("tier", p?.tier.id === t.id && "on")}
                    onClick={() => setQuantity(Math.max(t.min_quantity, t.contact_only ? t.min_quantity : t.min_quantity))}
                  >
                    {t.most_popular && (
                      <span className="popular">
                        <Badge tone="grad" plain>Popular</Badge>
                      </span>
                    )}
                    <div className="tier-name">{t.label}</div>
                    <div className="tier-price">
                      {t.unit_price_cents != null ? cents(t.unit_price_cents, t.currency) : "Contact"}
                      {t.unit_price_cents != null && <small> /design</small>}
                    </div>
                    <div className="tier-range">
                      {t.min_quantity.toLocaleString()}
                      {t.max_quantity ? `–${t.max_quantity.toLocaleString()}` : "+"} designs
                    </div>
                  </button>
                ))}
              </div>
            )}
            <div className="stack" style={{ gap: 10 }}>
              <div className="spread">
                <span className="label">Quantity</span>
                <div className="qty">
                  <Button aria-label="Fewer" onClick={() => setQuantity((n) => Math.max(1, n - (n > 100 ? 50 : 10)))} icon={<Minus />} />
                  <input
                    className="input"
                    type="number"
                    min={1}
                    max={1_000_000}
                    value={quantity}
                    onChange={(e) => setQuantity(Math.max(1, Math.min(1_000_000, Math.round(Number(e.target.value) || 1))))}
                    aria-label="Number of designs"
                  />
                  <Button aria-label="More" onClick={() => setQuantity((n) => n + (n >= 100 ? 50 : 10))} icon={<Plus />} />
                </div>
              </div>
              <input className="range" type="range" min={1} max={1000} step={1} value={Math.min(quantity, 1000)} onChange={(e) => setQuantity(Number(e.target.value))} aria-label="Quantity slider" />
            </div>
          </div>
        </Card>

        <Card title="Order summary" className="sticky">
          {price.error ? (
            <Alert tone="danger">{errorMessage(price.error)}</Alert>
          ) : !p ? (
            <Spinner />
          ) : contactOnly ? (
            <div className="stack">
              <p className="muted">
                {quantity.toLocaleString()} designs is an <b>{p.tier.label}</b> volume. Talk to us for a tailored price and invoicing.
              </p>
              <a className="btn btn-primary btn-block" href="mailto:sales@solaros.app?subject=Enterprise%20design%20credits">
                Contact sales
              </a>
            </div>
          ) : (
            <div className="stack">
              <div>
                <div className="total-line">
                  <span className="muted">
                    {p.quantity.toLocaleString()} × {cents(p.unit_price_cents, currency)} <span className="faint">({p.tier.label})</span>
                  </span>
                  <span>{cents(p.subtotal_cents, currency)}</span>
                </div>
                {p.discount_cents > 0 && (
                  <div className="total-line" style={{ color: "var(--ok)" }}>
                    <span>
                      <Sparkles size={13} style={{ verticalAlign: -2 }} /> First purchase −{p.discount_pct}% on {p.discounted_quantity}
                    </span>
                    <span>−{cents(p.discount_cents, currency)}</span>
                  </div>
                )}
                <div className="total-line big">
                  <span>Total</span>
                  <span>{cents(p.total_cents, currency)}</span>
                </div>
                <div className="hint right">{cents(Math.round(p.total_cents / p.quantity), currency)} per design on average</div>
              </div>
              {checkout.error && <Alert tone="danger">{errorMessage(checkout.error)}</Alert>}
              <Button variant="primary" size="lg" block icon={<CreditCard />} disabled={!canManage} loading={checkout.isPending} onClick={() => checkout.mutate()}>
                Continue to payment
              </Button>
              <p className="hint">{canManage ? "Secure checkout. Credits are added as soon as the payment is confirmed." : "Only owners and admins can buy credits."}</p>
            </div>
          )}
        </Card>
      </div>

      <div style={{ marginTop: 24 }}>
        <Card title="History" pad={false}>
          {credits.data.entries.length === 0 ? (
            <Empty icon={<Receipt />} title="No activity yet" />
          ) : (
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Type</th>
                    <th className="hide-sm">Note</th>
                    <th className="num">Credits</th>
                    <th className="num hide-sm">Expires</th>
                  </tr>
                </thead>
                <tbody>
                  {credits.data.entries.map((e) => (
                    <tr key={e.id}>
                      <td className="nowrap">{date(e.created_at, true)}</td>
                      <td>
                        <Badge tone={KIND_LABEL[e.kind].tone}>{KIND_LABEL[e.kind].label}</Badge>
                      </td>
                      <td className="hide-sm muted ellipsis" style={{ maxWidth: 360 }}>
                        {e.note || (e.design_id ? `Design ${e.design_id.slice(0, 8)}` : "—")}
                      </td>
                      <td className="num" style={{ fontWeight: 600, color: e.amount > 0 ? "var(--ok)" : undefined }}>
                        {e.amount > 0 ? `+${e.amount}` : e.amount}
                      </td>
                      <td className="num hide-sm faint">{e.expires_at ? date(e.expires_at) : "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </div>
    </>
  );
}
